import { motion } from 'framer-motion';
import { Film, Award, Calendar, Globe, Sparkles, Clapperboard } from 'lucide-react';
import Layout from '@/components/Layout';
import { CURRENT_FILMS, PAST_FILMS, UPCOMING_FILMS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function Filmography() {
  const currentFilm = CURRENT_FILMS[0];
  const epicUniverses = UPCOMING_FILMS.filter(f =>
    f.id === 'pc-cinematic-universe' || f.id === 'supreme-consciousness' || f.id === 'nawabistan'
  );
  const upcomingProjects = UPCOMING_FILMS.filter(f =>
    f.id !== 'pc-cinematic-universe' && f.id !== 'supreme-consciousness' && f.id !== 'nawabistan'
  );

  const filmLinks: Record<string, string> = {
    'catfight': 'https://r39zsfpb3h.skywork.website',
    'chaos-afra-tafri': 'https://afratafri-movie.com',
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.KESHHAV_ONSET_ON_SET}
              alt="Keshhav Panneriy on set"
              className="w-full h-full object-cover object-bottom opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/60 to-background" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-10 md:mb-16"
            >
              <h1 className="font-display text-3xl sm:text-5xl md:text-7xl font-bold mb-4 md:mb-6 text-foreground">
                FILMOGRAPHY
              </h1>
              <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
                A Journey Through Cinematic Excellence
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-background to-secondary/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Clapperboard className="w-8 h-8 text-accent" />
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  CURRENT PRODUCTION
                </h2>
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-accent to-accent/50 rounded-full" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card className="overflow-hidden border-accent/20 shadow-gold bg-card/50 backdrop-blur">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8">
                    <div className="relative aspect-[2/3] md:aspect-auto">
                      <img
                        src={IMAGES[currentFilm.poster as keyof typeof IMAGES]}
                        alt={currentFilm.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                    </div>
                    <div className="p-5 md:p-8 lg:p-12 flex flex-col justify-center">
                      <Badge className="w-fit mb-3 md:mb-4 bg-accent text-accent-foreground">
                        IN PRODUCTION
                      </Badge>
                      <h3 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 text-foreground">
                        {currentFilm.title}
                      </h3>
                      <p className="text-base md:text-xl text-accent font-semibold mb-4 md:mb-6 italic">
                        {currentFilm.tagline}
                      </p>
                      <p className="text-sm md:text-lg text-muted-foreground mb-5 md:mb-8 leading-relaxed">
                        {currentFilm.description}
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className="flex items-center gap-2">
                          <Film className="w-5 h-5 text-accent" />
                          <span className="text-sm text-foreground">{currentFilm.genre}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Globe className="w-5 h-5 text-accent" />
                          <span className="text-sm text-foreground">6 Countries</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-accent" />
                          <span className="text-sm text-foreground">70+ Min VFX</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-accent" />
                          <span className="text-sm text-foreground">{currentFilm.year}</span>
                        </div>
                      </div>
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                          Star Cast
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {currentFilm.cast?.map((actor, idx) => (
                            <Badge key={idx} variant="outline" className="border-accent/30">
                              {actor}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {currentFilm.highlights && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                            Production Highlights
                          </h4>
                          <ul className="space-y-2">
                            {currentFilm.highlights.map((highlight, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <span className="text-accent mt-1">•</span>
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* CHAOS website link */}
                      <div className="mt-6">
                        <a
                          href="https://afratafri-movie.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-bold px-6 py-3 rounded hover:bg-accent/80 transition-colors"
                        >
                          🎬 VISIT OFFICIAL WEBSITE ↗
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-8 h-8 text-accent" />
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  PAST ACHIEVEMENTS
                </h2>
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-accent to-accent/50 rounded-full" />
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
              {PAST_FILMS.map((film, idx) => (
                <motion.div
                  key={film.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                >
                  <Card className="overflow-hidden border-border hover:border-accent/50 transition-all duration-300 shadow-cinematic hover:shadow-gold bg-card/50 backdrop-blur h-full">
                    <CardContent className="p-0">
                      {film.poster && (
                        <div className="relative aspect-[2/3] overflow-hidden">
                          <img
                            src={IMAGES[film.poster as keyof typeof IMAGES]}
                            alt={film.title}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                          {filmLinks[film.id] && (
                            <a
                              href={filmLinks[film.id]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute bottom-3 right-3 bg-accent text-accent-foreground text-xs font-bold px-3 py-1.5 rounded hover:bg-accent/80 transition-colors z-10"
                            >
                              VIEW FILM ↗
                            </a>
                          )}
                        </div>
                      )}
                      <div className="p-3 md:p-6">
                        <Badge className="mb-2 md:mb-3 bg-secondary text-secondary-foreground">
                          {film.year}
                        </Badge>
                        <h3 className="font-display text-base md:text-2xl font-bold mb-2 text-foreground">
                          {film.title}
                        </h3>
                        <p className="text-sm text-accent font-semibold mb-3 italic">
                          {film.tagline}
                        </p>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                          {film.description}
                        </p>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-xs">
                            <Film className="w-4 h-4 text-accent" />
                            <span className="text-foreground">{film.genre}</span>
                          </div>
                          {film.duration && (
                            <div className="flex items-center gap-2 text-xs">
                              <Calendar className="w-4 h-4 text-accent" />
                              <span className="text-foreground">{film.duration}</span>
                            </div>
                          )}
                          {film.awards && (
                            <div className="flex items-center gap-2 text-xs">
                              <Award className="w-4 h-4 text-accent" />
                              <span className="text-foreground">{film.awards}</span>
                            </div>
                          )}
                        </div>
                        {film.highlights && (
                          <div className="border-t border-border pt-4">
                            <ul className="space-y-1">
                              {film.highlights.slice(0, 3).map((highlight, hIdx) => (
                                <li key={hIdx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                  <span className="text-accent mt-0.5">•</span>
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {!film.poster && filmLinks[film.id] && (
                          <a
                            href={filmLinks[film.id]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-block bg-accent text-accent-foreground text-xs font-bold px-4 py-2 rounded hover:bg-accent/80 transition-colors"
                          >
                            VISIT FILM WEBSITE ↗
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-background to-secondary/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-8 h-8 text-accent" />
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  UPCOMING PROJECTS
                </h2>
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-accent to-accent/50 rounded-full" />
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {upcomingProjects.map((film, idx) => (
                <motion.div
                  key={film.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                >
                  <Card className="overflow-hidden border-border hover:border-accent/50 transition-all duration-300 bg-card/50 backdrop-blur h-full">
                    <CardContent className="p-0">
                      {film.poster && (
                        <div className="relative aspect-[2/3] overflow-hidden">
                          <img
                            src={IMAGES[film.poster as keyof typeof IMAGES]}
                            alt={film.title}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                          <Badge className="absolute top-3 left-3 bg-accent/20 text-accent border-accent/30 backdrop-blur" variant="outline">
                            PRE-PRODUCTION
                          </Badge>
                        </div>
                      )}
                      {!film.poster && (
                        <div className="relative h-40 bg-gradient-to-br from-secondary/50 to-background flex items-center justify-center">
                          <Film className="w-16 h-16 text-accent/30" />
                          <Badge className="absolute top-3 left-3 bg-accent/20 text-accent border-accent/30" variant="outline">
                            PRE-PRODUCTION
                          </Badge>
                        </div>
                      )}
                      <div className="p-3 md:p-5">
                        <h3 className="font-display text-sm md:text-xl font-bold mb-2 text-foreground">
                          {film.title}
                        </h3>
                        <p className="text-sm text-accent font-semibold mb-2 italic">
                          {film.tagline}
                        </p>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {film.description}
                        </p>
                        <div className="flex items-center gap-2 text-xs">
                          <Film className="w-4 h-4 text-accent" />
                          <span className="text-foreground">{film.genre}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-8 h-8 text-accent" />
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  EPIC UNIVERSES
                </h2>
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-accent to-accent/50 rounded-full" />
              <p className="text-lg text-muted-foreground mt-4 max-w-3xl">
                Unprecedented cinematic universes that redefine the boundaries of storytelling
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-8">
              {epicUniverses.map((film, idx) => (
                <motion.div
                  key={film.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: idx * 0.2 }}
                >
                  <Card className="overflow-hidden border-accent/30 shadow-gold bg-card/50 backdrop-blur h-full">
                    <CardContent className="p-0">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={IMAGES[film.poster as keyof typeof IMAGES]}
                          alt={film.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                        <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">
                          EPIC UNIVERSE
                        </Badge>
                      </div>
                      <div className="p-5 md:p-8">
                        <h3 className="font-display text-xl md:text-3xl font-bold mb-3 text-foreground">
                          {film.title}
                        </h3>
                        <p className="text-lg text-accent font-semibold mb-4 italic">
                          {film.tagline}
                        </p>
                        <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                          {film.description}
                        </p>
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          {film.duration && (
                            <div className="flex items-center gap-2">
                              <Film className="w-5 h-5 text-accent" />
                              <span className="text-sm text-foreground">{film.duration}</span>
                            </div>
                          )}
                          {film.budget && (
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-accent" />
                              <span className="text-sm text-foreground">{film.budget}</span>
                            </div>
                          )}
                          {film.releaseDate && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-accent" />
                              <span className="text-sm text-foreground">{film.releaseDate}</span>
                            </div>
                          )}
                          {film.grossEstimate && (
                            <div className="flex items-center gap-2">
                              <Globe className="w-5 h-5 text-accent" />
                              <span className="text-sm text-foreground">{film.grossEstimate}</span>
                            </div>
                          )}
                        </div>
                        {film.highlights && (
                          <div className="border-t border-border pt-6">
                            <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                              Universe Highlights
                            </h4>
                            <ul className="space-y-2">
                              {film.highlights.map((highlight, hIdx) => (
                                <li key={hIdx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <span className="text-accent mt-1">•</span>
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}